// SpaceContentDetailModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../../../util/api";

import {
    Backdrop,
    Box,
    Chip,
    Dialog,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { notify } from "../../../../../util/toast";

import CloseIcon from "@mui/icons-material/Close";

import ActionButton from "../../../../../component/common/ActionButton";
import { ds } from "../../../../../styles/designSystem";
import LoadingDots from "../../../../../component/common/LoadingDots";
import { safeImgUrl } from "../../../../../util/imageUrl";

/**
 * 3D 콘텐츠 상세 모달 — 레이아웃은 2D {@code KeyVisualModal} 과 동일.
 *
 * <h4>구조</h4>
 * <ol>
 *   <li>Header : 제목(이벤트명) + 위치/등록일 + <b>다운로드</b> + 닫기</li>
 *   <li>Themes/Keywords 칩 영역</li>
 *   <li>Image viewer (세로 스크롤)</li>
 *   <li>Footer : <b>수정</b> / <b>삭제</b> / <b>닫기</b></li>
 * </ol>
 *
 * @param {object}     item        - SpaceContentResponseDTO
 * @param {() => void} onClose
 * @param {() => void} [onEdit]    - "수정" 클릭 (호출자가 EditModal 열기)
 * @param {() => void} [onDelete]  - "삭제" 클릭 (호출자가 confirm + 삭제)
 * @param {() => void} [onDownload]
 * @param {boolean}    [downloading] - 다운로드 진행 중 (버튼 스피너)
 */
export default function SpaceContentDetailModal({
    item,
    onClose,
    onEdit,
    onDelete,
    onDownload,
    downloading = false,
}) {
    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const API_REAL = process.env.REACT_APP_API_REAL_URL;

    const eventName = item?.eventName;
    const location = item?.location;

    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);

    const mountedRef = useRef(false);
    useEffect(() => {
        mountedRef.current = true;
        return () => (mountedRef.current = false);
    }, []);

    const fetchImages = useCallback(async () => {
        if (!eventName || !location) return;
        try {
            setLoading(true);
            const data = await api.get(`${API_BASE}/space-content/files`, { event: eventName, location });
            const list = Array.isArray(data) ? data : (data?.data || []);
            if (mountedRef.current) setImages(list);
        } catch (e) {
            console.error(e);
            notify("이미지 목록을 불러오지 못했습니다.", "error");
            if (mountedRef.current) setImages([]);
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [API_BASE, eventName, location]);

    useEffect(() => { fetchImages(); }, [fetchImages]);

    const themes = useMemo(() => item?.themes || [], [item]);
    const keywords = useMemo(() => item?.keywords || [], [item]);
    const createdAtLabel = item?.createdAt
        ? String(item.createdAt).slice(0, 16).replace("T", " ")
        : null;

    if (!item) return null;

    return (
        <>
            <Dialog
                open
                disableEscapeKeyDown
                onClose={() => onClose?.()}
                fullWidth
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        borderRadius: 1.5,
                        border: `1px solid ${ds.colors.border}`,
                        boxShadow: "0 18px 60px rgba(15, 23, 42, 0.18)",
                        bgcolor: ds.colors.surface,
                        display: "flex",
                        height: "92vh",
                        maxHeight: "92vh",
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 2.5, py: 1.5,
                        bgcolor: "#fff",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            noWrap
                            sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}
                            title={eventName}
                        >
                            {eventName || "공간 콘텐츠"}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.2 }} useFlexGap flexWrap="wrap">
                            {item?.locationType && (
                                <Chip
                                    label={item.locationType}
                                    size="small"
                                    sx={ds.components.external.chip}
                                />
                            )}
                            {location && (
                                <Chip
                                    label={location}
                                    size="small"
                                    sx={ds.components.external.chip}
                                />
                            )}
                            {createdAtLabel && (
                                <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#64748b" }}>
                                    등록 {createdAtLabel}
                                </Typography>
                            )}
                        </Stack>
                    </Box>

                    {onDownload && (
                        <ActionButton
                            variant="download"
                            onClick={onDownload}
                            loading={downloading}
                            tooltip="클릭 시, 콘텐츠 파일을 ZIP 으로 다운로드합니다"
                        />
                    )}

                    <Tooltip title="닫기">
                        <IconButton size="small" onClick={() => onClose?.()} sx={{ color: "#64748b" }}>
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Themes / Keywords */}
                <Box sx={{ px: 2.5, py: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        {themes.length === 0 && keywords.length === 0 ? (
                            <Typography sx={{ color: "text.secondary", fontWeight: 800, fontSize: 13 }}>
                                등록된 테마/키워드가 없습니다.
                            </Typography>
                        ) : (
                            <>
                                {themes.map((t, i) => (
                                    <Chip
                                        key={`th-${i}`}
                                        label={String(t)}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            ...ds.components.external.chip,
                                            color: "#166534",
                                            borderColor: "rgba(22,101,52,0.35)",
                                        }}
                                    />
                                ))}
                                {keywords.map((k, i) => (
                                    <Chip
                                        key={`kw-${i}`}
                                        label={String(k)}
                                        size="small"
                                        variant="outlined"
                                        sx={ds.components.external.chip}
                                    />
                                ))}
                            </>
                        )}
                    </Stack>
                </Box>

                {/* Image viewer */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        p: 2.5,
                        bgcolor: ds.colors.surface,
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            borderRadius: 1.5,
                            borderColor: ds.colors.border,
                            bgcolor: ds.colors.bg,
                            overflow: "hidden",
                        }}
                    >
                        {images.length === 0 ? (
                            <Box sx={{ p: 6, textAlign: "center", color: "text.secondary", fontWeight: 900 }}>
                                표시할 이미지가 없습니다.
                            </Box>
                        ) : (
                            <Stack spacing={2} alignItems="center" sx={{ p: 2 }}>
                                {images.map((img, i) => (
                                    <Box
                                        key={`${img}-${i}`}
                                        component="img"
                                        src={safeImgUrl(
                                            img && (img.startsWith("http://") || img.startsWith("https://"))
                                                ? img
                                                : `${API_REAL}/${img}`.replaceAll("//", "/").replace(":/", "://")
                                        )}
                                        alt={`img-${i}`}
                                        sx={{
                                            width: "100%",
                                            maxWidth: 1100,
                                            height: "auto",
                                            borderRadius: 1.5,
                                            border: `1px solid ${ds.colors.border}`,
                                            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                                            bgcolor: ds.colors.surface,
                                            userSelect: "none",
                                        }}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Paper>
                </Box>

                {/* Footer — 2D 와 동일: 수정 / 삭제 / 닫기 */}
                <Box
                    sx={{
                        px: 2.5, py: 1.5,
                        borderTop: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "#fafbfc",
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ flex: 1 }} />

                    {onEdit && <ActionButton variant="edit" onClick={onEdit} />}
                    {onDelete && <ActionButton variant="delete" onClick={onDelete} />}
                    <ActionButton variant="close" onClick={() => onClose?.()} />
                </Box>
            </Dialog>

            <Backdrop
                open={loading}
                sx={{ zIndex: (t) => t.zIndex.modal + 10, bgcolor: "rgba(255,255,255,0.85)" }}
            >
                <LoadingDots />
            </Backdrop>
        </>
    );
}
